import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { Dropdown } from 'primereact/dropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook } from '@fortawesome/free-brands-svg-icons';
import { faWandMagicSparkles, faUndo } from '@fortawesome/free-solid-svg-icons';
import styles from '../../styles/BlogviewTTT.module.scss';
import { handleShare } from '../../utils/ExploreBlogTTTHandlers';

const ShareDialogContent = ({ selectedPost, shareData, isReadOnly }) => {
  const toast = useRef(null);
  const user_ID = parseInt(localStorage.getItem('user_ID')) || null;

  const initialMessage = useMemo(() => {
    const cleanAndFormatText = (html) => {
      if (!html) return '';
      let text = html;
      text = text.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
      text = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
      text = text.replace(/<li>/gi, '\n• ');
      text = text.replace(/<\/p>|<\/div>|<\/h[1-6]>|<\/li>|<\/ul>|<\/ol>/gi, '\n');
      text = text.replace(/<br\s*\/?>/gi, '\n');
      text = text.replace(/<[^>]+>/g, '');
      text = text.replace(/\/\*![\s\S]*?\*\//g, '');
      text = text.replace(/\.elementor-[\s\S]*?\{[\s\S]*?\}/g, '');
      text = text.replace(/[ \t]+/g, ' ');
      text = text.replace(/\n\s*\n/g, '\n\n');
      return text.trim();
    };
    return cleanAndFormatText(selectedPost?.post_content);
  }, [selectedPost]);

  const [message, setMessage] = useState('');
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [scheduledTime, setScheduledTime] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [statusAttachedMedia, setStatusAttachedMedia] = useState(null);
  const [selectedImageUrls, setSelectedImageUrls] = useState([]);
  
  const [generatedMessage, setGeneratedMessage] = useState(null);
  const [isShowingGenerated, setIsShowingGenerated] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const [backgroundPresets, setBackgroundPresets] = useState([]);
  // ----- MODIFIED: State now holds the full object or null -----
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [generatedModel, setGeneratedModel] = useState(null);

  // ----- MODIFIED: useEffect to handle data loading and state setting correctly -----
  useEffect(() => {
    // Set initial states based on shareData or reset for a new post
    if (shareData && isReadOnly) {
      setMessage(shareData.message || '');
      setIsScheduled(!!shareData.scheduled_date);
      setScheduledDate(shareData.scheduled_date ? new Date(shareData.scheduled_date) : null);
      setScheduledTime(shareData.scheduled_time ? shareData.scheduled_time.slice(0, 5) : '');
      setStatusAttachedMedia(shareData.status_attached_media);
      setSelectedImageUrls(shareData.image_urls ? JSON.parse(shareData.image_urls) : []);
      // We will set selectedBackground after fetching presets to ensure we have the object
    } else if (!isReadOnly) {
      setMessage(initialMessage || '');
      setIsScheduled(false);
      setScheduledDate(null);
      setScheduledTime('');
      setStatusAttachedMedia(null);
      setSelectedImageUrls([]);
      setGeneratedMessage(null);
      setIsShowingGenerated(false);
      setSelectedBackground(null); // Reset background
      setGeneratedModel(null);
    }

    const fetchBackgroundPresets = async () => {
      try {
        const response = await fetch('https://py.afaa.website/facebook/api/background');
        if (!response.ok) throw new Error(`API error! status: ${response.status}`);
        const data = await response.json();
        setBackgroundPresets(data);

        // Now that presets are fetched, find and set the selected background object if viewing a shared post
        if (shareData && isReadOnly && shareData.background_presets) {
          const presetId = shareData.background_presets;
          const foundPreset = data.find(p => p.id === presetId);
          setSelectedBackground(foundPreset || null);
        }
      } catch (error) {
        toast.current.show({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to fetch background presets: ${error.message}`,
          life: 5000,
        });
      }
    };
    fetchBackgroundPresets();
  }, [shareData, isReadOnly, initialMessage]);
  
  useEffect(() => {
    if (statusAttachedMedia !== 3) {
        setSelectedBackground(null);
    }
  }, [statusAttachedMedia]);

  const handleGenerateText = async () => {
    setIsGenerating(true);
    setGeneratedModel(null);
    const contentToSummarize = isShowingGenerated ? initialMessage : message;
    const prompt = `+ สรุปเนื้อหานี้พอสังเขป ให้เหมาะกับการโพสต์โซเชียลมีเดีย + ขอไม่เกิน 200 ตัวอักษร พร้อม emoji + พร้อมติด แฮชแท็ก #Hashtag + ในคำตอบไม่ต้องระบุจำนวนตัวอักษร + เนื้อหาที่ต้องการให้สรุป : ${contentToSummarize}`;

    try {
      const response = await fetch('https://py.afaa.website/generated/api/text/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });

      if (!response.ok) {
        throw new Error(`API error! status: ${response.status}`);
      }

      const data = await response.json();
      const newGeneratedText = data.generated_text;
      const newGeneratedModel = data.model_version;

      setGeneratedMessage(newGeneratedText);
      setMessage(newGeneratedText);
      setGeneratedModel(newGeneratedModel);
      setIsShowingGenerated(true);
      toast.current.show({ severity: 'success', summary: 'Success', detail: 'Text generated successfully!', life: 3000 });
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Error', detail: `Failed to generate text: ${error.message}`, life: 5000 });
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleMessageView = () => {
    if (isShowingGenerated) {
      setMessage(initialMessage);
      setIsShowingGenerated(false);
      setGeneratedModel(null);
    } else if (generatedMessage) {
      setMessage(generatedMessage);
      setIsShowingGenerated(true);
    }
  };

  const mediaOptions = [
    { label: 'Link URL', value: 1 },
    { label: 'Multimedia', value: 2 },
    { label: 'Text Only', value: 3 },
  ];

  if (!selectedPost && !shareData) {
    return <p>No content available to share or view.</p>;
  }

  const shareUrl = `https://blog.ttt-website.com/${selectedPost?.post_name || ''}`;

  // ----- MODIFIED: handleShareClick now sends the ID from the selectedBackground object -----
  const handleShareClick = async () => {
    if (isReadOnly || !statusAttachedMedia) {
      return;
    }
    setIsLoading(true);
    const finalMessage = message.replace(/\n\n/g, '\n\u200B\n');

    try {
      await handleShare({
        message: finalMessage,
        statusAttachedMedia,
        shareUrl,
        selectedImageUrls,
        isScheduled,
        scheduledDate,
        scheduledTime,
        selectedPost,
        user_ID,
        toast: toast.current,
        selectedBackground: selectedBackground ? selectedBackground.id : null, // Pass the background ID from the object
        generatedModel,
      });
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Error', detail: `Failed to share or upload: ${error.message}`, life: 5000 });
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    toast.current.show({ severity: 'success', summary: 'Copied', detail: 'URL copied to clipboard', life: 3000 });
  };

  const handleImageSelection = (guid) => {
    if (!isReadOnly) {
      setSelectedImageUrls((prev) =>
        prev.includes(guid) ? prev.filter((url) => url !== guid) : [...prev, guid]
      );
    }
  };

  const filteredBackgroundPresets = backgroundPresets.filter((preset) => preset.is_active !== false);

  const backgroundOptionTemplate = (option) => {
    if (!option) return null;
    return (
      <div style={{
        backgroundColor: option.html_color,
        color: '#FFFFFF',
        textShadow: '0 0 3px rgba(0,0,0,0.7)',
        padding: '0.5rem',
        borderRadius: '3px'
      }}>
        <span>{option.description_th} | {option.suggestion_th} </span>
        <small style={{ opacity: 0.85 }}>({option.description_en})</small>
      </div>
    );
  };

  // ----- MODIFIED: Simplified template, as 'option' is now the full object -----
  const selectedBackgroundTemplate = (option, props) => {
    if (option) {
      return backgroundOptionTemplate(option);
    }
    return <span>{props.placeholder}</span>;
  };

  return (
    <div className={styles.shareDialog}>
      <Toast ref={toast} />
      <h4>{shareData ? shareData.title : selectedPost?.post_title || 'No Title'}</h4>
      <p>{shareData ? 'Shared to Facebook' : 'Share this post to Facebook'}</p>
      <div className={styles.field}>
        <label htmlFor="mediaType">Share Type</label>
        <Dropdown
          id="mediaType"
          value={statusAttachedMedia}
          options={mediaOptions}
          onChange={(e) => !isReadOnly && setStatusAttachedMedia(e.value)}
          placeholder="Please select a share type"
          disabled={isReadOnly}
          className={styles.inputFullWidth}
        />
      </div>

      {statusAttachedMedia === 3 && (
        <div className={styles.field}>
          <label htmlFor="backgroundPreset">Background Presets</label>
          {/* ----- MODIFIED: Removed optionValue to store the full object in state ----- */}
          <Dropdown
            id="backgroundPreset"
            value={selectedBackground}
            options={filteredBackgroundPresets}
            onChange={(e) => !isReadOnly && setSelectedBackground(e.value)}
            optionLabel="description_th"
            placeholder="Select background preset"
            filter
            filterBy="description_th,description_en,suggestion_th"
            valueTemplate={selectedBackgroundTemplate}
            itemTemplate={backgroundOptionTemplate}
            className={styles.inputFullWidth}
            disabled={isReadOnly}
          />
        </div>
      )}

      <div className={styles.field}>
        <div className={styles.messageHeader}>
          <label htmlFor="message">Message</label>
          {!isReadOnly && (
            <div className={styles.messageActions}>
              {generatedMessage && (
                <Button
                  label={isShowingGenerated ? 'Use Original' : 'Use Generated'}
                  icon={<FontAwesomeIcon icon={faUndo} />}
                  className="p-button-text p-button-sm"
                  onClick={toggleMessageView}
                />
              )}
              <Button
                label="AA-Chats Generate"
                icon={<FontAwesomeIcon icon={faWandMagicSparkles} />}
                className="p-button-outlined p-button-sm"
                onClick={handleGenerateText}
                loading={isGenerating}
                disabled={isReadOnly || isGenerating}
              />
            </div>
          )}
        </div>
        <InputTextarea
          id="message"
          value={message}
          onChange={(e) => !isReadOnly && setMessage(e.target.value)}
          rows={6}
          placeholder="Enter your share message or generate one with AI. Press Enter twice for a blank line."
          className={styles.inputFullWidth}
          autoResize
          disabled={isReadOnly || isGenerating}
        />
      </div>
      {statusAttachedMedia === 1 && (
        <div className={styles.field}>
          <label htmlFor="shareUrl">Share URL</label>
          <div className={styles.inputGroup}>
            <InputText
              id="shareUrl"
              value={shareData ? shareData.link_URL_content : shareUrl}
              readOnly={isReadOnly}
              onClick={(e) => !isReadOnly && e.target.select()}
              disabled={isReadOnly}
            />
            {!isReadOnly && (
              <Button
                icon="pi pi-copy"
                className="p-button-secondary"
                onClick={() => copyToClipboard(shareUrl)}
                tooltip="Copy to clipboard"
              />
            )}
          </div>
        </div>
      )}
      {statusAttachedMedia === 2 && (selectedPost?.images || (shareData && JSON.parse(shareData.image_urls || '[]').length > 0)) && (
        <div className={styles.field}>
          <label>Select Images</label>
          <div className={styles.imageSelection}>
            {(selectedPost?.images || JSON.parse(shareData?.image_urls || '[]')).map((img, index) => (
              <div key={index} className={styles.imageOption}>
                {!isReadOnly && (
                  <input
                    type="checkbox"
                    id={`image-${img.guid || index}`}
                    checked={selectedImageUrls.includes(img.guid || img)}
                    onChange={() => handleImageSelection(img.guid || img)}
                  />
                )}
                <label htmlFor={`image-${img.guid || index}`}>
                  <img src={img.guid || img} alt={selectedPost?.post_title || `Shared image ${index + 1}`} className={styles.previewImage} />
                </label>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className={`${styles.field} ${styles.switchContainer}`}>
        <label htmlFor="scheduleSwitch">Schedule Post</label>
        <InputSwitch
          id="scheduleSwitch"
          checked={isScheduled}
          onChange={(e) => !isReadOnly && setIsScheduled(e.value)}
          disabled={isReadOnly}
        />
      </div>
      {isScheduled && (
        <>
          <div className={styles.field}>
            <label htmlFor="scheduledDate">Schedule Date</label>
            <Calendar
              id="scheduledDate"
              value={scheduledDate}
              onChange={(e) => !isReadOnly && setScheduledDate(e.value)}
              dateFormat="yy-mm-dd"
              showIcon
              placeholder="Select date"
              minDate={new Date()}
              className={styles.inputFullWidth}
              disabled={isReadOnly}
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="scheduledTime">Schedule Time</label>
            <InputText
              id="scheduledTime"
              type="time"
              value={scheduledTime || ''}
              onChange={(e) => !isReadOnly && setScheduledTime(e.target.value)}
              className={styles.inputFullWidth}
              disabled={isReadOnly}
            />
          </div>
        </>
      )}
      <hr className={styles.divider} />
      <Button
        label={isReadOnly ? 'View Details' : 'Share on Facebook'}
        icon={<FontAwesomeIcon icon={faFacebook} />}
        className={`${styles.facebookButton} p-button-primary`}
        onClick={handleShareClick}
        disabled={isReadOnly || isLoading || (!isReadOnly && (isScheduled && (!scheduledDate || !scheduledTime)))}
        loading={isLoading}
      />
    </div>
  );
};

export default ShareDialogContent;